import React, { useEffect, useState } from "react";
import BookmarkIconButton from "./BookmarkIconButton";
import { makeBookmarkPost } from "../utils/bookmarkPost";
import assetUrl from "../utils/assetUrl";
import {
  getHomeBannerSlides,
  getDefaultHomeBannerSlides,
  HOME_BANNER_UPDATED_EVENT,
  hydrateHomeBannerFromRemote,
} from "../utils/homeBannerStore";

const arrowIcon = assetUrl("/assets/images/home/rethink-ways-travel-arrow.svg");

const normalizeSlide = (slide = {}) => ({
  id: String(slide.id || ""),
  image: assetUrl(String(slide.image || "")),
  thumb: slide.thumb ? assetUrl(String(slide.thumb || "")) : "",
  title: String(slide.title || ""),
  tag: String(slide.tag || ""),
  link: String(slide.link || ""),
  tagLink: String(slide.tagLink || ""),
  mobilePosition: String(slide.mobilePosition || ""),
  headline: String(slide.headline || ""),
  mobileHeadline: String(slide.mobileHeadline || ""),
  sub: String(slide.sub || ""),
  cta: String(slide.cta || ""),
});

const readStoredSlides = () => {
  const next = getHomeBannerSlides({ includeStoredOverrides: true });
  const fallback = getDefaultHomeBannerSlides();
  const list = Array.isArray(next) && next.length ? next : fallback;
  return list.map(normalizeSlide);
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const CtaPill = ({ label, href }) => (
  <a
    href={href}
    onClick={(e) => e.stopPropagation()}
    className="group/pill inline-flex items-center gap-3 h-[40px] md:h-[44px] px-4 md:px-5 rounded-full bg-[#D02525] border-2 border-[#D02525] text-white text-[13px] md:text-[14px] font-semibold transition-colors duration-300 group-hover:bg-black group-hover:border-[#D02525]"
    style={{ fontFamily: "Poppins, sans-serif" }}
  >
    <img
      src={arrowIcon}
      alt={`${label} arrow icon`}
      className="w-[16px] h-[16px] rotate-[-45deg] brightness-0 invert transition-transform duration-300 group-hover:rotate-0"
    />
    {label}
  </a>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function Banner({ darkMode }) {
  const [bannerSlides, setBannerSlides] = useState(readStoredSlides);
  const detectDarkMode = () => {
    if (typeof document === "undefined") return false;

    return (
      document.documentElement.classList.contains("trw-dark-mode") ||
      document.body.classList.contains("trw-dark-mode") ||
      document.body.classList.contains("dark")
    );
  };

  const normalizeSlug = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const getCategorySlug = (slide) => {
    const link = String(slide?.tagLink || "");
    if (link.includes("/category/")) {
      return link.split("/").filter(Boolean).pop() || "";
    }
    return normalizeSlug(slide?.tag || "");
  };
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isThemeDark, setIsThemeDark] = useState(detectDarkMode);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const resolvedDarkMode = typeof darkMode === "boolean" ? darkMode : isThemeDark;

  useEffect(() => {
    void hydrateHomeBannerFromRemote();
    const refresh = () => {
      const next = readStoredSlides();
      setBannerSlides(next);
      setCurrentSlide(0);
    };
    window.addEventListener("storage", refresh);
    window.addEventListener(HOME_BANNER_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(HOME_BANNER_UPDATED_EVENT, refresh);
    };
  }, []);

  // Auto-advance every 5 seconds
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentSlide((value) => (value + 1) % (bannerSlides.length || 1));
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [bannerSlides.length]);

  // Track viewport width for thumb sizing
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const syncTheme = () => setIsThemeDark(detectDarkMode());
    const observer = new MutationObserver(syncTheme);

    syncTheme();
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const activeSlide = bannerSlides[currentSlide] || bannerSlides[0];
  const activeCopy = {
    headline: activeSlide?.headline || "",
    mobileHeadline: activeSlide?.mobileHeadline || activeSlide?.headline || "",
    sub: activeSlide?.sub || "",
    cta: activeSlide?.cta || "",
  };

  const stopSlideClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /** Render a single slide image (stacked, cross-fade via opacity) */
  const renderSlideImage = (slide, index, mobile) => {
    const isActive = index === currentSlide;
    const isPriority = index === 0;
    const transitionClass =
      isPriority && currentSlide === 0 ? "" : "transition-opacity duration-500 ease-out";
    const visibilityClass = isActive
      ? "opacity-100 z-10"
      : "opacity-0 z-0 pointer-events-none";

    return (
      <img
        key={index}
        src={slide.image}
        srcSet={
          slide.thumb ? `${slide.thumb} 800w, ${slide.image} 1600w` : undefined
        }
        sizes={
          slide.thumb
            ? mobile
              ? "100vw"
              : "(max-width: 1500px) 100vw, 1500px"
            : undefined
        }
        alt={slide.title}
        width="1600"
        height="900"
        loading={isPriority ? "eager" : "lazy"}
        fetchPriority={isPriority ? "high" : "low"}
        decoding={isPriority ? "sync" : "async"}
        className={
          mobile
            ? `absolute ${transitionClass} ${visibilityClass}`
            : `absolute inset-0 w-full h-full object-cover ${transitionClass} ${visibilityClass}`
        }
        style={
          mobile
            ? {
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: slide.mobilePosition || "center center",
            }
            : {
              objectPosition:
                index === 0
                  ? viewportWidth < 1480
                    ? "center top"
                    : "center top"
                  : "center center",
            }
        }
      />
    );
  };

  /** Thumbnail navigation controls */
  const renderThumbControls = (desktop) => {
    if (desktop && viewportWidth >= 768) {
      const isWide = viewportWidth >= 1200;
      const size = isWide ? 100 : 72;

      return (
        <div
          className="absolute right-0 bottom-0 z-30 flex -translate-x-1/2 items-end gap-3"
          style={{
            right: isWide ? "-240px" : "-175px",
            bottom: "30px",
          }}
        >
          {bannerSlides.map((slide, index) => (
            <button
              key={index}
              onClick={(e) => {
                stopSlideClick(e);
                setCurrentSlide(index);
              }}
              aria-label={`Show banner slide ${index + 1}`}
              aria-pressed={index === currentSlide}
              style={{ height: `${size}px`, width: `${size}px`, borderRadius: "10px" }}
              className={`relative overflow-hidden rounded-[10px] transition-all duration-300 ${isWide ? "h-[100px] w-[100px]" : "h-[72px] w-[72px]"
                } ${index === currentSlide
                  ? "shadow-[0_10px_30px_rgba(0,0,0,0.35)] scale-[1.02]"
                  : "opacity-90 hover:opacity-100"
                }`}
            >
              <img
                src={slide.thumb || slide.image}
                alt={`${slide.tag} banner thumbnail`}
                width={size}
                height={size}
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                style={{
                  height: `${size}px`,
                  width: `${size}px`,
                  objectFit: "cover",
                  objectPosition: "bottom right",
                  borderRadius: "10px",
                }}
                className={`absolute inset-0 ${isWide ? "h-[100px] w-[100px]" : "h-[72px] w-[72px]"
                  } object-cover rounded-[10px]`}
              />
            </button>
          ))}
        </div>
      );
    }

    // Mobile dot-like thumbs
    return (
      <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-end gap-2">
        {bannerSlides.map((slide, index) => (
          <button
            key={index}
            onClick={(e) => {
              stopSlideClick(e);
              setCurrentSlide(index);
            }}
            aria-label={`Show banner slide ${index + 1}`}
            aria-pressed={index === currentSlide}
            style={{ height: "44px", width: "44px", borderRadius: "10px" }}
            className={`relative overflow-hidden rounded-[10px] transition-all duration-300 h-[44px] w-[44px] ${index === currentSlide
                ? "shadow-[0_8px_20px_rgba(0,0,0,0.3)] scale-[1.02] opacity-100"
                : "opacity-85 hover:opacity-100"
              }`}
          >
            <img
              src={slide.thumb || slide.image}
              alt={`${slide.tag} banner thumbnail`}
              width={44}
              height={44}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
              style={{
                height: "44px",
                width: "44px",
                objectFit: "cover",
                objectPosition: "bottom right",
                borderRadius: "10px",
              }}
              className="absolute inset-0 h-[44px] w-[44px] object-cover rounded-[10px]"
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`w-full sm:max-w-[1500px] top-0 mx-auto px-0 sm:px-4 lg:px-5 xl:px-6 mt-0 md:mt-[55px] lg:mt-[58px] xl:mt-[62px] 2xl:mt-[50px] transition-colors duration-300 ${resolvedDarkMode ? "bg-black" : "bg-white"
        }`}
    >
      {/* ── Mobile layout ── */}
      <div
        className="block md:hidden cursor-pointer"
        onClick={() => (window.location.href = activeSlide.link)}
      >
        {/* Image area */}
        <div className="relative w-full h-[350px] overflow-hidden">
          {bannerSlides.map((slide, index) => renderSlideImage(slide, index, true))}

          {/* Tag badge */}
          <a
            href={activeSlide.tagLink || activeSlide.link}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 left-0 bg-[#D02525] text-white text-[12px] font-semibold uppercase px-3 py-[4px] z-20 tracking-wider"
          >
            {activeSlide.tag}
          </a>

          {/* Bookmark button */}
          <BookmarkIconButton
            post={makeBookmarkPost({
              link: activeSlide.link,
              title: activeCopy.mobileHeadline || activeCopy.headline,
              excerpt: activeCopy.sub,
              imageSrc: activeSlide.image,
              imageAlt: activeCopy.mobileHeadline || activeCopy.headline,
            })}
            categoryLabel={activeSlide.tag}
            categorySlug={getCategorySlug(activeSlide)}
            categoryLink={activeSlide.tagLink}
            className={`absolute top-3 right-3 z-20 shadow-md ${resolvedDarkMode ? "bg-black/85 border border-white/10" : "bg-white"
              }`}
            size={44}
            iconSize={20}
          />

          {renderThumbControls(false)}
        </div>

        {/* Text area */}
        <div
          className="px-4 pt-5 pb-5"
          style={{
            height: "240px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
            backgroundColor: resolvedDarkMode ? "#000000" : "#f2f2f2",
          }}
        >
          <h1
            className="text-[26px] leading-[1.35] font-['Yeseva_One'] text-center w-full mb-2"
            style={{
              flexShrink: 0,
              color: resolvedDarkMode ? "#ffffff" : "#151618",
              whiteSpace: "pre-line",
            }}
          >
            {activeCopy.mobileHeadline}
          </h1>

          {activeCopy.sub && (
            <p
              className="text-[17px] leading-[1.5] text-center w-full"
              style={{
                fontFamily: "Poppins, sans-serif",
                flexShrink: 0,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                color: resolvedDarkMode ? "#aaaaaa" : "#5B5F62",
              }}
            >
              {activeCopy.sub}
            </p>
          )}

          <div className="mt-auto">
            <CtaPill label={activeCopy.cta || "READ MORE"} href={activeSlide.link} />
          </div>
        </div>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden md:block relative group">
        <div
          className="block cursor-pointer"
          onClick={() => (window.location.href = activeSlide.link)}
        >
          <div
            className="trw-banner-frame relative w-full h-[480px] lg:h-[580px] xl:h-[620px] 2xl:h-[650px] rounded-[16px] shadow-lg"
            style={{ isolation: "isolate" }}
          >
            {/* Images + overlays */}
            <div className="absolute inset-0 overflow-hidden rounded-[16px] bg-white">
              {bannerSlides.map((slide, index) => renderSlideImage(slide, index, false))}

              {/* Dark gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/0" />

              {/* Tag badge */}
              <a
                href={activeSlide.tagLink || activeSlide.link}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-0 tracking-wider left-10 bg-[#D02525] text-white text-[14px] font-semibold uppercase px-2 py-[3px] z-20"
              >
                {activeSlide.tag}
              </a>

              {/* Bookmark button */}
              <BookmarkIconButton
                post={makeBookmarkPost({
                  link: activeSlide.link,
                  title: activeCopy.headline,
                  excerpt: activeCopy.sub,
                  imageSrc: activeSlide.image,
                  imageAlt: activeCopy.headline,
                })}
                categoryLabel={activeSlide.tag}
                categorySlug={getCategorySlug(activeSlide)}
                categoryLink={activeSlide.tagLink}
                className={`absolute top-6 right-6 z-20 shadow-md ${resolvedDarkMode ? "bg-black/85 border border-white/10" : "bg-white"
                  }`}
                size={44}
                iconSize={20}
              />
            </div>

            {/* Headline + CTA */}
            <div
              className="absolute left-8 md:left-10 bottom-[80px] md:bottom-[100px] lg:bottom-[115px] xl:bottom-[124px] 2xl:bottom-[135px] max-w-[90%] md:max-w-[620px] lg:max-w-[720px] xl:max-w-[760px] 2xl:max-w-[820px] text-white z-30 pointer-events-none"
            >
              <h2
                className="text-[32px] md:text-[38px] lg:text-[50px] xl:text-[54px] 2xl:text-[58px] leading-[1.2] font-['Yeseva_One'] mb-3 md:mb-4"
                style={{ whiteSpace: "pre-line" }}
              >
                {activeCopy.headline}
              </h2>

              {activeCopy.sub && (
                <p
                  className="text-[16px] md:text-[18px] lg:text-[21px] xl:text-[22px] 2xl:text-[24px] text-white/85 leading-[1.5] mb-4 md:mb-6"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                >
                  {activeCopy.sub}
                </p>
              )}

              <div style={{ pointerEvents: "auto" }}>
                <CtaPill label={activeCopy.cta || "READ MORE"} href={activeSlide.link} />
              </div>
            </div>

            {renderThumbControls(true)}
          </div>
        </div>
      </div>
    </div>
  );
}
